import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  School as CoachIcon,
  Business as ClubIcon,
  AccountTree as InterclubIcon,
  Check as CheckIcon,
  EmojiEvents as SkaterIcon,
  Gavel as TermsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { fetchInterclubs, fetchClubs } from '../services/clubService';
import { REGISTRATION_BASE_URL } from '../config/environment';
import registrationService from '../services/registrationService';
import usfsService from '../services/usfsService';
import USFSLinkDialog from './USFSLinkDialog';
import {
  mergeAdultIcmLevelsIntoProfileOptions,
  ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE,
} from '../utils/skatingLevels';

const ROLES = [
  {
    id: 'parent',
    label: 'Parent/Guardian',
    description: 'I only manage my children’s skating — I am not competing as a skater myself',
    icon: <GroupIcon />,
    requiresApproval: false,
  },
  {
    id: 'skater',
    label: 'Adult Skater',
    description: 'I compete myself (including adults in any test level) — choose this even if you also have kids in skating',
    icon: <SkaterIcon />,
    requiresApproval: false,
  },
  {
    id: 'coach',
    label: 'Coach',
    description: 'I coach skaters and want to follow their progress',
    icon: <CoachIcon />,
    requiresApproval: true,
  },
  {
    id: 'club_admin',
    label: 'Club Representative',
    description: 'I manage skaters for my club',
    icon: <ClubIcon />,
    requiresApproval: true,
  },
  {
    id: 'interclub_admin',
    label: 'Interclub Administrator',
    description: 'I manage an interclub series',
    icon: <InterclubIcon />,
    requiresApproval: true,
  },
];

// Steps are dynamic based on whether user needs to add skaters
const getSteps = (selectedRoles) => {
  const baseSteps = ['Who You Are', 'Your Information', 'Your Series & Club'];
  const needsSkaterStep = selectedRoles.includes('parent') || selectedRoles.includes('skater');
  if (needsSkaterStep) {
    baseSteps.push('Add Skaters');
  }
  baseSteps.push('Terms of Service', 'Complete Setup');
  return baseSteps;
};

export default function OnboardingWizard({ open, onComplete }) {
  const { user, getIdToken } = useFirebaseAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedInterclub, setSelectedInterclub] = useState('');
  const [selectedClub, setSelectedClub] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [coachUsfsMemberId, setCoachUsfsMemberId] = useState('');

  // New interclub request (for interclub admins whose interclub doesn't exist)
  const [creatingNewInterclub, setCreatingNewInterclub] = useState(false);
  const [newInterclub, setNewInterclub] = useState({
    name: '',
    shortName: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
  });

  // Skaters (for parent roles - adding children)
  const [skaters, setSkaters] = useState([]);
  const [currentSkater, setCurrentSkater] = useState({
    firstName: '',
    lastName: '',
    membershipId: '',
    dateOfBirth: '',
  });

  // Adult skater's own info (when user is the skater themselves)
  const [selfSkaterInfo, setSelfSkaterInfo] = useState({
    membershipId: '',
    currentLevel: '',
    coachName: '',
  });
  // Pre-Preliminary: must acknowledge Excel Series ineligibility before proceeding
  const [prePrelimAcknowledged, setPrePrelimAcknowledged] = useState(false);

  // Coaches list for dropdown
  const [coaches, setCoaches] = useState([]);
  // USFS import (parent Add Skaters step or adult self-skater)
  const [usfsImportDialogOpen, setUsfsImportDialogOpen] = useState(false);
  const [usfsImportTarget, setUsfsImportTarget] = useState('child'); // 'child' | 'self' | 'coach'
  const [usfsImportLoading, setUsfsImportLoading] = useState(false);
  const [usfsImportError, setUsfsImportError] = useState('');

  // Compute dynamic steps based on selected roles
  const steps = getSteps(selectedRoles);
  const needsSkaterStep = selectedRoles.includes('parent') || selectedRoles.includes('skater');
  const isAdultSkaterOnly = selectedRoles.includes('skater') && !selectedRoles.includes('parent');

  // Data
  const [interclubs, setInterclubs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [filteredClubs, setFilteredClubs] = useState([]);

  // Levels for dropdown: interclub supportedLevels + always Adult 1–6 / Gold for adult skater profiles
  const selectedInterclubDoc = selectedInterclub ? interclubs.find((ic) => ic.id === selectedInterclub) : null;
  const levelOptions = useMemo(() => {
    if (!selectedInterclubDoc?.supportedLevels?.length) return ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE;
    const filtered = ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE.filter((level) =>
      selectedInterclubDoc.supportedLevels.includes(level)
    );
    return mergeAdultIcmLevelsIntoProfileOptions(filtered, ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE);
  }, [selectedInterclubDoc]);

  // Load interclubs and clubs
  useEffect(() => {
    const loadData = async () => {
      try {
        const [interclubData, clubData] = await Promise.all([fetchInterclubs(), fetchClubs()]);
        setInterclubs(interclubData || []);
        setClubs(clubData || []);
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load interclub and club data');
      }
    };
    if (open) {
      loadData();
    }
  }, [open]);

  // Filter clubs when interclub changes
  useEffect(() => {
    if (selectedInterclub) {
      const filtered = clubs.filter((club) => club.interclubId === selectedInterclub);
      setFilteredClubs(filtered);
      // Reset club selection if current selection is not in filtered list
      if (selectedClub && !filtered.find((c) => c.id === selectedClub)) {
        setSelectedClub('');
      }
    } else {
      setFilteredClubs([]);
      setSelectedClub('');
    }
  }, [selectedInterclub, clubs, selectedClub]);

  // Pre-fill with Google display name
  useEffect(() => {
    if (user?.displayName && !displayName) {
      setDisplayName(user.displayName);
      const parts = user.displayName.split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' ') || '');
    }
  }, [user, displayName]);

  const handleRoleToggle = (roleId) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    );
  };

  // Skater management functions
  const handleAddSkater = () => {
    if (!currentSkater.firstName.trim() || !currentSkater.lastName.trim()) {
      setError('Please enter the skater\'s first and last name');
      return;
    }
    if (!currentSkater.membershipId.trim()) {
      setError('Please enter the skater\'s USFS membership number');
      return;
    }
    setSkaters((prev) => [...prev, { ...currentSkater, id: Date.now() }]);
    setCurrentSkater({ firstName: '', lastName: '', membershipId: '', dateOfBirth: '' });
    setError(null);
  };

  const handleRemoveSkater = (skaterId) => {
    setSkaters((prev) => prev.filter((s) => s.id !== skaterId));
  };

  // Format USFS birthDate to YYYY-MM-DD for date input
  const formatBirthDateForInput = (birthDate) => {
    if (!birthDate) return '';
    const str = String(birthDate).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    if (str.includes('T')) return str.split('T')[0];
    if (str.includes('/')) {
      const parts = str.split('/');
      if (parts.length === 3) {
        const [m, d, y] = parts;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      }
    }
    try {
      const d = new Date(str);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    } catch (_) {}
    return '';
  };

  const handleUsfsImportSubmit = async ({ username, password }) => {
    setUsfsImportLoading(true);
    setUsfsImportError('');
    try {
      const token = await getIdToken(true);
      if (!token) throw new Error('Unable to get authentication token');
      const result = await usfsService.lookupMember({ username, password }, token);
      if (result?.success && result?.member) {
        const member = result.member;
        const membershipId = member.memberId || member.membershipId || username;
        if (usfsImportTarget === 'self') {
          setSelfSkaterInfo((prev) => ({ ...prev, membershipId: membershipId || prev.membershipId }));
          setUsfsImportDialogOpen(false);
        } else if (usfsImportTarget === 'coach') {
          setCoachUsfsMemberId(membershipId || '');
          setUsfsImportDialogOpen(false);
        } else {
          setCurrentSkater((prev) => ({
            ...prev,
            firstName: member.firstName || prev.firstName,
            lastName: member.lastName || prev.lastName,
            membershipId: membershipId || prev.membershipId,
            dateOfBirth: formatBirthDateForInput(member.birthDate) || prev.dateOfBirth,
          }));
          setUsfsImportDialogOpen(false);
        }
      } else {
        throw new Error(result?.error || 'Could not find member data');
      }
    } catch (err) {
      console.error('USFS import error:', err);
      setUsfsImportError(err.message || 'Import failed. Check credentials and try again.');
    } finally {
      setUsfsImportLoading(false);
    }
  };

  // Get current step name for validation
  const currentStepName = steps[activeStep];

  const handleNext = () => {
    setError(null);

    // Validate based on step name (since steps are dynamic)
    switch (currentStepName) {
      case 'Who You Are':
        if (selectedRoles.length === 0) {
          setError('Please select at least one role');
          return;
        }
        break;

      case 'Your Information':
        if (!displayName.trim()) {
          setError('Please enter your name');
          return;
        }
        break;

      case 'Your Series & Club':
        if (creatingNewInterclub) {
          if (!newInterclub.name.trim()) {
            setError('Please enter the interclub name');
            return;
          }
          if (!newInterclub.contactEmail.trim()) {
            setError('Please enter a contact email for the interclub');
            return;
          }
        } else {
          if (!selectedInterclub) {
            setError('Please select an interclub series');
            return;
          }
          if (!selectedClub) {
            setError('Please select a club');
            return;
          }
        }
        if (selectedRoles.includes('coach') && !(coachUsfsMemberId || '').trim()) {
          setError('USFS Membership ID is required when registering as a coach');
          return;
        }
        break;

      case 'Add Skaters':
        // When user is a skater (alone or with parent), require their own skating info
        if (selectedRoles.includes('skater')) {
          if (!selfSkaterInfo.membershipId.trim()) {
            setError('Please enter your US Figure Skating membership number');
            return;
          }
          if (!selfSkaterInfo.currentLevel) {
            setError('Please select your current level');
            return;
          }
          if (selfSkaterInfo.currentLevel === 'Pre-Preliminary' && !prePrelimAcknowledged) {
            setError('Please acknowledge the Pre-Preliminary / Excel Series notice to continue');
            return;
          }
        }
        // When user is a parent, require at least one child skater
        if (selectedRoles.includes('parent') && skaters.length === 0) {
          setError('Please add at least one skater');
          return;
        }
        break;

      case 'Terms of Service':
        if (!termsAccepted || !privacyAccepted) {
          setError('Please accept the Terms of Service and Privacy Policy to continue');
          return;
        }
        break;

      default:
        break;
    }

    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      // Build skaters array - different for adult skaters vs parents vs both
      let skatersToSubmit = [];
      const selfSkater = selectedRoles.includes('skater') && selfSkaterInfo.membershipId
        ? (() => {
            const s = {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              membershipId: selfSkaterInfo.membershipId.trim(),
              currentLevel: selfSkaterInfo.currentLevel,
              coachName: selfSkaterInfo.coachName?.trim() || '',
              isSelf: true,
            };
            if (selfSkaterInfo.currentLevel === 'Pre-Preliminary' && prePrelimAcknowledged && user) {
              s.prePrelimAcknowledgedAt = new Date().toISOString();
              s.prePrelimAcknowledgedBy = user.uid;
              s.prePrelimAcknowledgedByEmail = user.email || '';
            }
            return s;
          })()
        : null;
      const childrenSkaters = selectedRoles.includes('parent')
        ? skaters.map((s) => ({
            firstName: s.firstName.trim(),
            lastName: s.lastName.trim(),
            membershipId: s.membershipId?.trim() || '',
            dateOfBirth: s.dateOfBirth || null,
          }))
        : [];
      if (selfSkater && childrenSkaters.length > 0) {
        skatersToSubmit = [selfSkater, ...childrenSkaters];
      } else if (selfSkater) {
        skatersToSubmit = [selfSkater];
      } else {
        skatersToSubmit = childrenSkaters;
      }

      // Build request body
      const requestBody = {
        displayName: displayName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        roles: selectedRoles,
        interclubId: creatingNewInterclub ? null : selectedInterclub,
        clubId: creatingNewInterclub ? null : selectedClub,
        termsAccepted: true,
        privacyAccepted: true,
        skaters: skatersToSubmit,
        // Include self skater info when user is a skater (for profile update)
        selfSkaterInfo: selectedRoles.includes('skater') ? selfSkaterInfo : null,
      };
      if (selectedRoles.includes('coach')) {
        requestBody.coachUsfsMemberId = (coachUsfsMemberId || '').trim();
      }

      // If creating a new interclub, include the details
      if (creatingNewInterclub) {
        requestBody.newInterclub = {
          name: newInterclub.name.trim(),
          shortName: newInterclub.shortName.trim() || newInterclub.name.trim().substring(0, 10),
          description: newInterclub.description.trim(),
          contactEmail: newInterclub.contactEmail.trim(),
          contactPhone: newInterclub.contactPhone.trim(),
          website: newInterclub.website.trim(),
        };
      }

      const response = await fetch(`${REGISTRATION_BASE_URL}/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to complete onboarding');
      }

      const result = await response.json();
      // Pass selected roles so the parent can decide where to redirect
      onComplete?.({ ...result, selectedRoles });
    } catch (err) {
      console.error('Onboarding failed:', err);
      setError(err.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (stepName) => {
    switch (stepName) {
      case 'Who You Are':
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Welcome to Interclub Competition Manager!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Tell us about yourself so we can personalize your experience. Select all roles that apply to you.
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Competing yourself?
              </Typography>
              <Typography variant="body2" component="span">
                If <strong>you</strong> skate in competitions (including adult skaters), select <strong>Adult Skater</strong>. Only
                choose <strong>Parent/Guardian</strong> if you are registering <em>only</em> your children and you do not compete.
                You can select both if you compete and also manage your kids’ profiles.
              </Typography>
            </Alert>

            <FormGroup>
              {ROLES.map((role) => (
                <Paper
                  key={role.id}
                  elevation={selectedRoles.includes(role.id) ? 3 : 1}
                  sx={{
                    p: 2,
                    mb: 2,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: selectedRoles.includes(role.id) ? 'primary.main' : 'transparent',
                    bgcolor: selectedRoles.includes(role.id) ? 'primary.50' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                    },
                  }}
                  onClick={() => handleRoleToggle(role.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: selectedRoles.includes(role.id) ? 'primary.main' : 'grey.300',
                        width: 48,
                        height: 48,
                      }}
                    >
                      {role.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {role.label}
                        </Typography>
                        {role.requiresApproval && (
                          <Chip label="Requires Approval" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={selectedRoles.includes(role.id)}
                      onChange={() => handleRoleToggle(role.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                </Paper>
              ))}
            </FormGroup>
          </Box>
        );

      case 'Your Information':
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Your Information
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              This information will be used for your account profile.
            </Typography>

            <TextField
              fullWidth
              label="Full Name"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                const parts = e.target.value.split(' ');
                setFirstName(parts[0] || '');
                setLastName(parts.slice(1).join(' ') || '');
              }}
              required
              sx={{ mb: 3 }}
              helperText="This will be displayed on your profile"
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              helperText="Optional - for competition notifications"
              sx={{ mb: 2 }}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Your email ({user?.email}) will be used for account communications.
            </Alert>
          </Box>
        );

      case 'Your Series & Club':
        const isInterclubAdmin = selectedRoles.includes('interclub_admin');

        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Your Series & Club
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {creatingNewInterclub
                ? 'Enter your interclub series information for approval.'
                : 'Select the interclub series and club you are associated with.'}
            </Typography>

            {/* Option for interclub admins to create new interclub */}
            {isInterclubAdmin && !creatingNewInterclub && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Don't see your interclub series listed?{' '}
                  <Button
                    size="small"
                    onClick={() => {
                      setCreatingNewInterclub(true);
                      setSelectedInterclub('');
                      setSelectedClub('');
                    }}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
                  >
                    Request to add a new interclub
                  </Button>
                </Typography>
              </Alert>
            )}

            {creatingNewInterclub ? (
              // New interclub form
              <Box>
                <Button
                  size="small"
                  onClick={() => setCreatingNewInterclub(false)}
                  sx={{ mb: 2, textTransform: 'none' }}
                >
                  ← Back to select existing interclub
                </Button>

                <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    New Interclub Series Request
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Your request will be reviewed by our team. Once approved, you'll be able to manage your interclub.
                  </Typography>

                  <TextField
                    fullWidth
                    required
                    label="Interclub Name"
                    value={newInterclub.name}
                    onChange={(e) => setNewInterclub((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Pacific Northwest Interclub"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Short Name / Abbreviation"
                    value={newInterclub.shortName}
                    onChange={(e) => setNewInterclub((prev) => ({ ...prev, shortName: e.target.value }))}
                    placeholder="e.g., PNIC"
                    helperText="Optional - used for display in compact views"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={newInterclub.description}
                    onChange={(e) => setNewInterclub((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of your interclub series..."
                    sx={{ mb: 2 }}
                  />

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Contact Information
                  </Typography>

                  <TextField
                    fullWidth
                    required
                    label="Contact Email"
                    type="email"
                    value={newInterclub.contactEmail}
                    onChange={(e) => setNewInterclub((prev) => ({ ...prev, contactEmail: e.target.value }))}
                    placeholder="contact@interclub.org"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Contact Phone"
                    value={newInterclub.contactPhone}
                    onChange={(e) => setNewInterclub((prev) => ({ ...prev, contactPhone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Website"
                    value={newInterclub.website}
                    onChange={(e) => setNewInterclub((prev) => ({ ...prev, website: e.target.value }))}
                    placeholder="https://www.interclub.org"
                  />
                </Paper>

                <Alert severity="warning">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Pending Approval
                  </Typography>
                  <Typography variant="body2">
                    Your interclub request and admin role will be reviewed by our team. You'll receive a notification
                    once approved.
                  </Typography>
                </Alert>
              </Box>
            ) : (
              // Existing interclub selection
              <>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Interclub Series</InputLabel>
                  <Select
                    value={selectedInterclub}
                    onChange={(e) => setSelectedInterclub(e.target.value)}
                    label="Interclub Series"
                  >
                    {interclubs
                      .filter((ic) => ic.isActive !== false)
                      .map((interclub) => (
                        <MenuItem key={interclub.id} value={interclub.id}>
                          {interclub.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }} disabled={!selectedInterclub}>
                  <InputLabel>Club</InputLabel>
                  <Select
                    value={selectedClub}
                    onChange={(e) => setSelectedClub(e.target.value)}
                    label="Club"
                  >
                    {filteredClubs
                      .filter((c) => c.isActive !== false)
                      .map((club) => (
                        <MenuItem key={club.id} value={club.id}>
                          {club.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {selectedInterclub && filteredClubs.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      No clubs found for this interclub series
                    </Typography>
                  )}
                </FormControl>

                {selectedRoles.includes('coach') && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          component="img"
                          src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
                          alt="U.S. Figure Skating"
                          sx={{ height: 32, width: 'auto' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          Import and link your coach account from US Figure Skating
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setUsfsImportTarget('coach');
                          setUsfsImportError('');
                          setUsfsImportDialogOpen(true);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Import
                      </Button>
                    </Box>
                    <TextField
                      fullWidth
                      required
                      label="USFS Membership ID (required for coaches)"
                      value={coachUsfsMemberId}
                      onChange={(e) => setCoachUsfsMemberId(e.target.value)}
                      placeholder="Your USFS member number"
                      helperText="Required to link your coach record. Enter your US Figure Skating membership number."
                      sx={{ mb: 3 }}
                    />
                  </>
                )}

                {selectedRoles.some((r) => ['coach', 'club_admin', 'interclub_admin'].includes(r)) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Approval Required
                    </Typography>
                    <Typography variant="body2">
                      Your selected role(s) require approval from an interclub administrator. You'll have limited
                      access until approved.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>
        );

      case 'Add Skaters': {
        // Shared block: "Your Skating Information" (used for skater-only and skater+parent)
        const renderSelfSkaterSection = () => (
          <>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Your Skating Information
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Enter your US Figure Skating membership details.
            </Typography>

            {/* Import from US Figure Skating (adult self) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                bgcolor: 'grey.100',
                borderRadius: 2,
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="img"
                  src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
                  alt="U.S. Figure Skating"
                  sx={{ height: 32, width: 'auto' }}
                />
                <Typography variant="body2" color="text.secondary">
                  Import your membership number from US Figure Skating
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setUsfsImportTarget('self');
                  setUsfsImportError('');
                  setUsfsImportDialogOpen(true);
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Import
              </Button>
            </Box>

            <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <SkaterIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{displayName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                required
                label="US Figure Skating Number"
                value={selfSkaterInfo.membershipId}
                onChange={(e) => setSelfSkaterInfo((prev) => ({ ...prev, membershipId: e.target.value }))}
                placeholder="e.g., 1234567"
                helperText="Your USFS membership ID"
                sx={{ mb: 3 }}
              />

              <FormControl fullWidth required sx={{ mb: 3 }}>
                <InputLabel>Current Level</InputLabel>
                <Select
                  value={selfSkaterInfo.currentLevel}
                  onChange={(e) => {
                    const newLevel = e.target.value;
                    setSelfSkaterInfo((prev) => ({ ...prev, currentLevel: newLevel }));
                    if (newLevel !== 'Pre-Preliminary') setPrePrelimAcknowledged(false);
                  }}
                  label="Current Level"
                >
                  {levelOptions.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Coach Name"
                value={selfSkaterInfo.coachName}
                onChange={(e) => setSelfSkaterInfo((prev) => ({ ...prev, coachName: e.target.value }))}
                placeholder="Your coach's name"
                helperText="Optional - enter your primary coach's name"
                sx={{ mb: selfSkaterInfo.currentLevel === 'Pre-Preliminary' ? 2 : 0 }}
              />

              {selfSkaterInfo.currentLevel === 'Pre-Preliminary' && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Important: Pre-Preliminary Level Notice
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Skaters at the <strong>Pre-Preliminary</strong> level may want to compete in Excel Series events, however doing so will make them
                    <strong> ineligible for our interclub championship series</strong>. Pre-Preliminary skaters who compete in Excel will be disqualified from qualifying for championships.
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    If you are unsure about the correct level or competition eligibility, please contact your club administrator before proceeding.
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={prePrelimAcknowledged}
                        onChange={(e) => setPrePrelimAcknowledged(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="I understand that Pre-Preliminary skaters who compete in Excel Series will be ineligible for the interclub championship series"
                  />
                </Alert>
              )}
            </Paper>

            {isAdultSkaterOnly && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your club is set to the one you selected: <strong>{filteredClubs.find((c) => c.id === selectedClub)?.name || 'Selected club'}</strong>
              </Alert>
            )}
          </>
        );

        // Adult skater only - just their own skating info
        if (isAdultSkaterOnly) {
          return <Box>{renderSelfSkaterSection()}</Box>;
        }

        // Skater + parent: show "Your Skating Information" then "Add Your Skaters" (children)
        const isSkaterAndParent = selectedRoles.includes('skater') && selectedRoles.includes('parent');
        if (isSkaterAndParent) {
          return (
            <Box>
              {renderSelfSkaterSection()}
              <Divider sx={{ my: 4 }} />
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                Add Your Skaters (Children)
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Add the skaters you will be managing. You can add more later.
              </Typography>
              {skaters.length > 0 && (
                <List dense sx={{ mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  {skaters.map((skater) => (
                    <ListItem key={skater.id}>
                      <ListItemText
                        primary={`${skater.firstName} ${skater.lastName}`}
                        secondary={skater.membershipId ? `USFS #${skater.membershipId}` : 'No USFS number yet'}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveSkater(skater.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
              <Paper sx={{ p: 3, bgcolor: 'background.paper', border: '2px dashed', borderColor: 'grey.300' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      component="img"
                      src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
                      alt="U.S. Figure Skating"
                      sx={{ height: 32, width: 'auto' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Import skater info from US Figure Skating
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setUsfsImportTarget('child');
                      setUsfsImportError('');
                      setUsfsImportDialogOpen(true);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Import
                  </Button>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonAddIcon color="primary" />
                  {skaters.length === 0 ? 'Add a Skater' : 'Add Another Skater'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    value={currentSkater.firstName}
                    onChange={(e) => setCurrentSkater((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First name"
                  />
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    value={currentSkater.lastName}
                    onChange={(e) => setCurrentSkater((prev) => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Last name"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    fullWidth
                    required
                    label="USFS Membership Number"
                    value={currentSkater.membershipId}
                    onChange={(e) => setCurrentSkater((prev) => ({ ...prev, membershipId: e.target.value }))}
                    placeholder="e.g. 1234567"
                    helperText="Required - verifies skater information"
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    value={currentSkater.dateOfBirth}
                    onChange={(e) => setCurrentSkater((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    helperText="Optional - can be added later"
                  />
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddSkater}>
                  + ADD SKATER
                </Button>
              </Paper>
              {skaters.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Please add at least one skater (your child) to continue.
                </Alert>
              )}
            </Box>
          );
        }

        // Parent only - add children as skaters
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Add Your Skaters
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Add the skaters you will be managing. You can add more later.
            </Typography>

            {/* List of added skaters */}
            {skaters.length > 0 && (
              <Paper sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <List dense>
                  {skaters.map((skater) => (
                    <ListItem key={skater.id}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <SkaterIcon />
                      </Avatar>
                      <ListItemText
                        primary={`${skater.firstName} ${skater.lastName}`}
                        secondary={skater.membershipId ? `USFS #${skater.membershipId}` : 'No USFS number yet'}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => handleRemoveSkater(skater.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Add skater form */}
            <Paper sx={{ p: 3, bgcolor: 'background.paper', border: '2px dashed', borderColor: 'grey.300' }}>
              {/* Import from US Figure Skating */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    component="img"
                    src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
                    alt="U.S. Figure Skating"
                    sx={{ height: 32, width: 'auto' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Import skater info from US Figure Skating
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setUsfsImportTarget('child');
                    setUsfsImportError('');
                    setUsfsImportDialogOpen(true);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Import
                </Button>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon color="primary" />
                {skaters.length === 0 ? 'Add a Skater' : 'Add Another Skater'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={currentSkater.firstName}
                  onChange={(e) => setCurrentSkater((prev) => ({ ...prev, firstName: e.target.value }))}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={currentSkater.lastName}
                  onChange={(e) => setCurrentSkater((prev) => ({ ...prev, lastName: e.target.value }))}
                  required
                />
              </Box>

              <TextField
                fullWidth
                required
                label="USFS Membership Number"
                value={currentSkater.membershipId}
                onChange={(e) => setCurrentSkater((prev) => ({ ...prev, membershipId: e.target.value }))}
                placeholder="e.g. 1234567"
                helperText="Required - verifies skater information"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                type="date"
                label="Date of Birth"
                value={currentSkater.dateOfBirth}
                onChange={(e) => setCurrentSkater((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional - can be added later"
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSkater}
                disabled={!currentSkater.firstName.trim() || !currentSkater.lastName.trim() || !currentSkater.membershipId.trim()}
              >
                Add Skater
              </Button>
            </Paper>

            {skaters.length === 0 && (
              <Alert severity="info" sx={{ mt: 3 }}>
                Please add at least one skater to continue.
              </Alert>
            )}
          </Box>
        );
      }

      case 'Terms of Service':
        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Terms of Service
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Please review and accept our terms to continue.
            </Typography>

            <Paper
              sx={{
                p: 3,
                mb: 3,
                maxHeight: 250,
                overflow: 'auto',
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Terms of Service Summary
              </Typography>
              <Typography variant="body2" paragraph>
                By using Interclub Competition Manager, you agree to the following:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>You will provide accurate information about yourself and any skaters you register.</li>
                <li>You are responsible for maintaining the confidentiality of your account.</li>
                <li>You will not misuse the platform or attempt to access unauthorized data.</li>
                <li>Competition results and scores are provided for informational purposes.</li>
                <li>We may update these terms and will notify you of significant changes.</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                For the complete terms, visit our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                  Terms of Service
                </a>{' '}
                page.
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Privacy Policy Summary
              </Typography>
              <Typography variant="body2" paragraph>
                We collect and use your information to:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>Create and manage your account and skater profiles.</li>
                <li>Track competition registrations and results.</li>
                <li>Send important notifications about competitions and your account.</li>
                <li>Improve our services and user experience.</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                For complete details, visit our{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                  Privacy Policy
                </a>{' '}
                page.
              </Typography>
            </Paper>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                      Terms of Service
                    </a>
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                      Privacy Policy
                    </a>
                  </Typography>
                }
              />
            </FormGroup>
          </Box>
        );

      case 'Complete Setup':
        const selectedInterclubNameFinal = creatingNewInterclub
          ? newInterclub.name
          : interclubs.find((ic) => ic.id === selectedInterclub)?.name;
        const selectedClubNameFinal = creatingNewInterclub ? null : filteredClubs.find((c) => c.id === selectedClub)?.name;
        const pendingRoles = selectedRoles.filter((r) =>
          ROLES.find((role) => role.id === r)?.requiresApproval
        );

        return (
          <Box>
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Ready to Go!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Review your information and click "Complete Setup" to finish.
            </Typography>

            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Your Profile
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              {phone && (
                <Typography variant="body2" color="text.secondary">
                  {phone}
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Roles
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {selectedRoles.map((roleId) => {
                  const role = ROLES.find((r) => r.id === roleId);
                  return (
                    <Chip
                      key={roleId}
                      label={role?.label}
                      color={role?.requiresApproval ? 'warning' : 'primary'}
                      size="small"
                    />
                  );
                })}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {creatingNewInterclub ? 'New Interclub Request' : 'Series & Club'}
              </Typography>
              <Typography variant="body1">
                {selectedInterclubNameFinal}
                {creatingNewInterclub && (
                  <Chip label="Pending Approval" size="small" color="warning" sx={{ ml: 1 }} />
                )}
              </Typography>
              {selectedClubNameFinal && (
                <Typography variant="body2" color="text.secondary">
                  {selectedClubNameFinal}
                </Typography>
              )}
              {creatingNewInterclub && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Contact: {newInterclub.contactEmail}
                </Typography>
              )}

              {/* Show skating info when user is a skater */}
              {selectedRoles.includes('skater') && selfSkaterInfo.membershipId && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Your Skating Info
                  </Typography>
                  <Typography variant="body2">
                    <strong>USFS #:</strong> {selfSkaterInfo.membershipId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Level:</strong> {selfSkaterInfo.currentLevel}
                  </Typography>
                  {selfSkaterInfo.coachName && (
                    <Typography variant="body2">
                      <strong>Coach:</strong> {selfSkaterInfo.coachName}
                    </Typography>
                  )}
                </>
              )}

              {/* Show skaters (children) when any were added */}
              {skaters.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Skaters ({skaters.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {skaters.map((skater) => (
                      <Chip
                        key={skater.id}
                        icon={<SkaterIcon />}
                        label={`${skater.firstName} ${skater.lastName}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>

            {(pendingRoles.length > 0 || creatingNewInterclub) && (
              <Alert severity="info" icon={<CheckIcon />}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Almost there!
                </Typography>
                <Typography variant="body2">
                  {creatingNewInterclub ? (
                    <>
                      Your new interclub series request and Interclub Admin role will be reviewed by our team. You'll
                      receive a notification once approved.
                    </>
                  ) : (
                    <>
                      Your{' '}
                      {pendingRoles
                        .map((r) => ROLES.find((role) => role.id === r)?.label)
                        .join(', ')}{' '}
                      role(s) will be pending approval. You can still use the app as a{' '}
                      {selectedRoles.includes('parent') ? 'parent' : 'user'} while waiting.
                    </>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      fullScreen
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientShift 15s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("/competition.jpg") center/cover no-repeat',
              opacity: 0.08,
              pointerEvents: 'none',
            },
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              maxWidth: 700,
              p: 4,
              borderRadius: 3,
              position: 'relative',
              zIndex: 1,
              backdropFilter: 'blur(10px)',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Logo/Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Interclub Competition Manager
              </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Step Content */}
            <Box sx={{ minHeight: 400 }}>{getStepContent(currentStepName)}</Box>

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button disabled={activeStep === 0 || loading} onClick={handleBack} variant="outlined">
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {activeStep === steps.length - 1 ? 'Complete Setup' : 'Continue'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <USFSLinkDialog
        open={usfsImportDialogOpen}
        onClose={() => {
          setUsfsImportDialogOpen(false);
          setUsfsImportError('');
        }}
        onSubmit={handleUsfsImportSubmit}
        loading={usfsImportLoading}
        error={usfsImportError}
      />
    </Dialog>
  );
}
